import React, { useEffect, useRef } from 'react';
import { Avatar, Button, Spinner, Toaster } from '../common';
import { withUploader } from '../../hoc';
import { useAuth } from '../../contexts/AuthContext';
import {
  useDeleteAvatar,
  useUpdateAvatar,
  useUploadAvatar,
} from '../../hooks/useProfile';
import type { WithUploaderProps } from '../../types/uploader';
import toast from 'react-hot-toast';
import { Bin, Upload } from 'iconoir-react';

const AvatarUpload = (props: WithUploaderProps) => {
  const { addFiles, clearErrors, errors, config } = props;

  const { user, checkStatus } = useAuth();
  const { mutateAsync: uploadAvatar, isPending: isUploadingAvatar } =
    useUploadAvatar();
  const { mutateAsync: updateAvatar, isPending: isUpdatingAvatar } =
    useUpdateAvatar();
  const { mutateAsync: deleteAvatar, isPending: isDeletingAvatar } =
    useDeleteAvatar();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    clearErrors();
    const selectedFiles = e.target.files;
    if (selectedFiles) {
      addFiles(selectedFiles, () => {
        handleUploadAvatar(selectedFiles[0]);
      });
    }
    e.target.value = '';
  };

  const handleUploadAvatar = async (file: File) => {
    if (!file) return;

    await uploadAvatar(file, {
      onSuccess: async responseUploadAvatar => {
        await updateAvatar(
          {
            url: responseUploadAvatar.result.url,
          },
          {
            onSuccess: () => {
              toast.custom(t => (
                <Toaster
                  message='Profile picture has been updated successfully'
                  type='success'
                  id={t.id}
                />
              ));
            },
            onError: error => {
              toast.custom(t => (
                <Toaster
                  message={error?.message as string}
                  type='error'
                  id={t.id}
                />
              ));
            },
            onSettled: async () => {
              await checkStatus();
            },
          }
        );
      },
      onError: error => {
        toast.custom(t => (
          <Toaster message={error?.message as string} type='error' id={t.id} />
        ));
      },
    });
  };

  const handleRemoveAvatar = async () => {
    if (!user?.avatar || user?.avatar === null || user?.avatar === '') {
      return;
    }

    await deleteAvatar(
      {
        url: user?.avatar as string,
      },
      {
        onSuccess: () => {
          toast.custom(t => (
            <Toaster
              message='Profile picture has been removed successfully'
              type='success'
              id={t.id}
            />
          ));
        },
        onError: error => {
          toast.custom(t => (
            <Toaster
              message={error?.message as string}
              type='error'
              id={t.id}
            />
          ));
        },
        onSettled: async () => {
          await checkStatus();
        },
      }
    );
  };

  useEffect(() => {
    if (errors.length > 0) {
      toast.custom(t => (
        <Toaster message={errors.join(', ')} type='error' id={t.id} />
      ));
    }
  }, [errors]);

  return (
    <div className='md:flex items-start gap-6 mb-6'>
      <div className='relative'>
        <Avatar
          size='6xl'
          shape='square'
          name={user?.first_name + ' ' + user?.last_name}
          src={user?.avatar}
          onClick={handleClick}
        />
        {(isUploadingAvatar || isUpdatingAvatar) && (
          <div className='absolute top-0 left-0 w-full h-full flex items-center justify-center bg-black/40 rounded-2xl max-w-41'>
            <Spinner size='sm' color='primary' />
          </div>
        )}
      </div>
      <div className='flex items-center gap-2 grow md:mt-0 mt-4'>
        <Button
          className='w-full'
          variant='outline'
          onClick={handleClick}
          disabled={isUploadingAvatar || isUpdatingAvatar}
        >
          {/* Visually hidden and triggered by the surrounding button, so it has
              no visible label of its own to associate. aria-label is the right
              tool here; it was previously announced as an unlabelled file
              field. aria-hidden would be wrong, since the input is the actual
              control the picker opens. */}
          <input
            ref={fileInputRef}
            type='file'
            aria-label='Upload a new profile picture'
            multiple={config?.maxFiles && config?.maxFiles > 1 ? true : false}
            onChange={handleFileSelect}
            className='hidden'
            accept={config?.acceptedTypes?.join(',') || '*/*'}
          />
          <Upload className='w-6 h-6' />
          Upload new
        </Button>
        <Button
          className='w-full !bg-red-100 !text-red-700 !border-red-100 active:!bg-red-50 !shadow-none'
          disabled={
            !user?.avatar || user?.avatar === null || user?.avatar === ''
          }
          onClick={handleRemoveAvatar}
          isLoading={isDeletingAvatar}
        >
          <Bin className='w-6 h-6' />
          Remove
        </Button>
      </div>
    </div>
  );
};

export default withUploader(AvatarUpload);
